import React, { useMemo, useState, useCallback } from 'react';
import {
  FormControl, InputLabel, Select, MenuItem, Box, Chip, Typography,
  ListSubheader, TextField, InputAdornment, SelectChangeEvent, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import {
  ModelOption,
  ratingBars,
  getGroupedModelOptions,
  getAllModelOptions,
  MODEL_REGISTRY,
  isZDREligibleForRegion,
} from '../config/modelConfig';
import { useZdrStatus } from '../hooks/useZdrStatus';

interface ModelSelectorProps {
  multiple?: boolean;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options?: ModelOption[];
  size?: 'small' | 'medium';
  label?: string;
  showRatings?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  /** Multiple only: the first model is the default (starred, others can be made default). Off for plain sets. */
  ordered?: boolean;
}

const RatingBar: React.FC<{ icon: string; rating?: number; color: string; title: string }> = ({
  icon, rating, color, title,
}) => {
  const bars = ratingBars(rating);
  if (!bars.length) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }} title={title}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', lineHeight: 1, minWidth: 12 }}>
        {icon}
      </Typography>
      <Box sx={{ display: 'flex', gap: '1.5px' }}>
        {bars.map((filled, i) => (
          <Box key={i} sx={{ width: 5, height: 9, borderRadius: '1px', bgcolor: filled ? color : 'action.disabledBackground' }} />
        ))}
      </Box>
    </Box>
  );
};

const ModelItem: React.FC<{ model: ModelOption; showRatings: boolean; isGrouped: boolean }> = ({
  model, showRatings, isGrouped,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, width: '100%', py: 0.25 }}>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" noWrap sx={{ fontSize: '0.85rem' }}>{model.label}</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      {showRatings && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <RatingBar icon="$" rating={model.costRating} color="#ef6c00" title={`Cost: ${model.costRating}/5`} />
          <RatingBar icon="⚡" rating={model.speedRating} color="#2e7d32" title={`Speed: ${model.speedRating}/5`} />
          <RatingBar icon="IQ" rating={model.intelligenceRating} color="#1565c0" title={`Intelligence: ${model.intelligenceRating}/5`} />
        </Box>
      )}
      {model.category !== 'text' && (
        <Chip label={model.capabilityLabel} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
      )}
      {!isGrouped && (
        <Chip label={model.provider} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
      )}
    </Box>
  </Box>
);

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  multiple = false,
  value,
  onChange,
  options: optionsProp,
  size = 'medium',
  label = 'Model',
  showRatings = true,
  fullWidth = true,
  helperText,
  ordered = true,
}) => {
  const [search, setSearch] = useState('');

  // When the customer is in Zero Data Retention mode, hide every option
  // not eligible for their data region. Filter (don't disable) — a
  // dropdown full of disabled rows looks broken.
  const { enabled: zdrEnabled, region_mode: zdrRegion } = useZdrStatus();

  const allOptions = useMemo(() => {
    const base = optionsProp ?? getAllModelOptions();
    return zdrEnabled ? base.filter(o => isZDREligibleForRegion(o.value, zdrRegion)) : base;
  }, [optionsProp, zdrEnabled, zdrRegion]);

  const grouped = useMemo(() => getGroupedModelOptions(allOptions), [allOptions]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return grouped;
    const q = search.toLowerCase();
    return grouped
      .map(g => ({
        ...g,
        models: g.models.filter(
          m => m.label.toLowerCase().includes(q) || m.value.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q),
        ),
      }))
      .filter(g => g.models.length > 0);
  }, [grouped, search]);

  const handleChange = useCallback((e: SelectChangeEvent<string | string[]>) => {
    onChange(e.target.value as any);
  }, [onChange]);

  // The first selected model is the default; these edit the order without reopening the menu.
  const selectedList = useMemo(() => (multiple && Array.isArray(value) ? value : []), [multiple, value]);
  const makeDefault = useCallback((apiName: string) => {
    onChange([apiName, ...selectedList.filter(v => v !== apiName)]);
  }, [onChange, selectedList]);
  const removeModel = useCallback((apiName: string) => {
    onChange(selectedList.filter(v => v !== apiName));
  }, [onChange, selectedList]);

  const displayLabel = useCallback((apiName: string) => {
    const info = MODEL_REGISTRY[apiName];
    return info?.displayName ?? apiName;
  }, []);

  const providerForChip = useCallback((apiName: string) => {
    const info = MODEL_REGISTRY[apiName];
    return info?.provider;
  }, []);

  const providerChipColor = useCallback((provider?: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info' => {
    switch (provider) {
      case 'Google': return 'primary';
      case 'OpenAI': return 'success';
      case 'Anthropic': return 'warning';
      case 'Meta': return 'info';
      default: return 'default';
    }
  }, []);

  const menuItems = useMemo(() => {
    const items: React.ReactNode[] = [];

    items.push(
      <ListSubheader key="__search" sx={{ bgcolor: 'background.paper', pt: 1, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search models..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
        />
      </ListSubheader>,
    );

    for (const group of filteredGroups) {
      items.push(
        <ListSubheader
          key={`__hdr_${group.provider}`}
          sx={{
            bgcolor: 'background.paper',
            fontWeight: 700,
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'text.secondary',
            lineHeight: '28px',
            mt: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {group.provider}
        </ListSubheader>,
      );
      for (const model of group.models) {
        items.push(
          <MenuItem key={model.value} value={model.value} sx={{ py: 0.75, px: 2 }}>
            <ModelItem model={model} showRatings={showRatings} isGrouped />
          </MenuItem>,
        );
      }
    }
    return items;
  }, [filteredGroups, search, showRatings]);

  return (
    <FormControl fullWidth={fullWidth} size={size}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={multiple}
        value={value}
        label={label}
        onChange={handleChange}
        MenuProps={{
          PaperProps: { sx: { maxHeight: 420 } },
          autoFocus: false,
          disableAutoFocusItem: true,
        }}
        renderValue={(selected) => {
          if (multiple && Array.isArray(selected) && !ordered) {
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map(v => (
                  <Chip
                    key={v}
                    label={displayLabel(v)}
                    size="small"
                    color={providerChipColor(providerForChip(v))}
                    variant="outlined"
                    onMouseDown={(e) => e.stopPropagation()}
                    onDelete={() => removeModel(v)}
                  />
                ))}
              </Box>
            );
          }
          if (multiple && Array.isArray(selected)) {
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((v, i) => (
                  <Tooltip
                    key={v}
                    title={i === 0 ? 'Default: used when a request doesn’t name a model' : 'Click to make this the default'}
                  >
                    <Chip
                      label={i === 0 && selected.length > 1 ? `${displayLabel(v)} · Default` : displayLabel(v)}
                      size="small"
                      color={providerChipColor(providerForChip(v))}
                      variant={i === 0 ? 'filled' : 'outlined'}
                      icon={i === 0 ? <StarIcon sx={{ fontSize: 14 }} /> : undefined}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={i === 0 ? undefined : () => makeDefault(v)}
                      onDelete={() => removeModel(v)}
                    />
                  </Tooltip>
                ))}
              </Box>
            );
          }
          return displayLabel(selected as string);
        }}
      >
        {menuItems}
      </Select>
      {zdrEnabled && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
          🔒 Privacy mode is ON — only no-train models{zdrRegion === 'us' ? ' pinned to US regions' : ''} are shown.
        </Typography>
      )}
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
      {multiple && ordered && selectedList.length > 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          The starred model is the default. Click another model to make it the default; × removes one.
        </Typography>
      )}
    </FormControl>
  );
};

export default ModelSelector;
